/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['submitted', 'processing', 'shipped', 'returned', 'cancelled'] }] */
import { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { deepEqual } from 'fast-equals';

import { cn } from 'helpers/classnames';
import { ZAPPOS_MERCHANTID } from 'constants/crossSiteMerchantIdMapping';
import { CS_CANCELLED, CS_COMPLETED, CS_DECLINED_CANCELLED, CS_RETURNED } from 'constants/orderStatuses';
import { MartyContext } from 'utils/context';
import { updateOrderWithTrackingInfo } from 'actions/account/order';
import { getIsExchangeEligible } from 'actions/account/exchanges';
import { isDesktop } from 'helpers/ClientUtils';
import { capitalize } from 'helpers';
import { getAmethystPageType } from 'helpers/analytics';
import { groupBy, toArray } from 'helpers/lodashReplacement';
import {
  applyReturnTrackingStatusToCompositeStatus,
  applyReturnTrackingStatusToLegacyStatus,
  getCurrentShipment,
  getCurrentShipmentMessage,
  getCurrentShipmentNumber,
  getLegacyStatusFromCompositeStatus,
  getTotalShipmentsNumber,
  hasOrderBeenDelivered,
  hasOrderBeenShipped,
  hasShipmentBeenDelivered,
  hasTracking,
  isOrderCancellable,
  isOrderExchangeable,
  isOrderGeneratedByExchange,
  isOrderReturnable
} from 'helpers/MyAccountUtils';
import { getItemStatusClassName } from 'helpers/MyAccountRenderUtils';
import TrackingProgress from 'components/account/TrackingProgress';
import OrderProductForExchanges from 'components/account/Order/OrderProductForExchanges';
import GiftWrapIndicator from 'components/account/GiftWrapIndicator';
import OrderTableActionButtonsHeader from 'components/account/Order/OrderTableActionButtonsHeader';
import OrderTableSelectionColumn from 'components/account/Order/OrderTableSelectionColumn';
import { track } from 'apis/amethyst';
import { evOrderDetailShipmentClick, evOrderHistoryCancelReturnClick } from 'events/account';
import { pvSelectItemsToExchange } from 'events/exchanges';
import ShippingStatus from 'components/account/ShippingStatus';
import { ORDER_HISTORY_ACTION_TYPE } from 'constants/amethystEnums';
import { ORDER_ACTION_EXCHANGE, ORDER_ACTION_NONE, ORDER_ACTION_RETURN, ORDER_PAGE_STEPS } from 'constants/orderActions';
import ReturnsUtils from 'helpers/ReturnsUtils';
import { getMarketNameViaMerchantId } from 'helpers/ProductUtils';
import { addDaysToDateAndFormat, isTodayWithinNDaysAfterDate, safeParseOrderDate } from 'helpers/dateUtils';
import InfoOutlineSmallIcon from 'components/common/IconsV2/InfoOutlineSmallIcon';

import css from 'styles/components/account/orderTableLayout.scss';

export const isFitExempt = state => {
  switch (state) {
    case 'submitted':
    case 'processing':
    case 'cancelled':
      return true;
    default:
      return false;
  }
};

const shouldDisplayCheckboxesColumn = (currentStep, lineItems, keepExchangeOrdersFromReturnCancel) =>
  isOrderCancellable(lineItems, keepExchangeOrdersFromReturnCancel) ||
  (isOrderReturnable(lineItems, keepExchangeOrdersFromReturnCancel) && currentStep === ORDER_PAGE_STEPS.RETURN_STEP) ||
  (isOrderExchangeable(lineItems) && currentStep === ORDER_PAGE_STEPS.EXCHANGE_STEP);

const handleShipmentClick = (order, shipmentId, compositeStatus) => {
  track(() => [evOrderDetailShipmentClick, { order, shipmentId, compositeStatus }]);
};

const renderItemStatus = (
  trackingNumber,
  legacy,
  itemState,
  orderId,
  name,
  shipmentId,
  currentStep,
  isItemExchangeable,
  phoneNumber,
  compositeStatus,
  testId,
  order,
  planName
) => {
  const { shipments } = order;
  const { trackingAvailable } = getCurrentShipment(shipmentId, shipments);
  if (currentStep === ORDER_PAGE_STEPS.EXCHANGE_STEP && !isItemExchangeable) {
    return (
      <>
        <span className={getItemStatusClassName('cancelled')} data-test-id={testId('itemStatus')}>
          {capitalize('Exchange Unavailable')}
        </span>
        <div className={css.unavailableStatus}>
          This item cannot be exchanged at this time. If you have any questions please call customer service at <b>{phoneNumber}</b>.
        </div>
      </>
    );
  } else {
    return (
      <ShippingStatus
        status={itemState}
        orderId={orderId}
        onShipmentClick={handleShipmentClick.bind(this, order, shipmentId, compositeStatus)}
        shipmentId={shipmentId}
        totalShipments={getTotalShipmentsNumber(shipments)}
        trackingAvailable={trackingAvailable}
        currentShipmentNumber={getCurrentShipmentNumber(shipmentId, shipments)}
        shipmentMessage={getCurrentShipmentMessage(shipmentId, shipments)}
        planName={planName}
      />
    );
  }
};

const fitTypes = [
  { val: '0', name: 'Perfect' },
  { val: '1', name: 'Too Big' },
  { val: '-1', name: 'Too Small' }
];

export const FitSurveyOptions = ({ indicator, onSelectFitQuestion, isFitSurveyLoading = false }) =>
  fitTypes.map(type => {
    const { val, name } = type;
    const selected = indicator && indicator === val;
    return (
      <button
        key={val}
        onClick={onSelectFitQuestion}
        disabled={isFitSurveyLoading}
        className={cn({ [css.loadingButton]: isFitSurveyLoading })}
        type="button"
        data-selected={selected}
        value={val}
      >
        {name}
      </button>
    );
  });

export const makeTrackingProgressBar = ({ latestArrivalDate, order }) => {
  const { lineItems } = order;
  const isOrderShipped = hasOrderBeenShipped(lineItems);
  const isOrderDelivered = hasOrderBeenDelivered(lineItems);

  if (!hasTracking(order)) {
    return null;
  }

  return (
    <div className={css.trackingProgress}>
      <TrackingProgress isShipped={isOrderShipped} isDelivered={isOrderDelivered} estimatedDeliveryDate={latestArrivalDate} />
    </div>
  );
};

export const makeTrackingLink = ({ shipmentId, trackingNumber, orderId, testId }) => (
  <div className={css.trackPackage}>
    TRACK:{' '}
    <Link to={`/shipments/${orderId}/${shipmentId}`} data-test-id={testId('trackingLink')}>
      {trackingNumber}
    </Link>
  </div>
);

export class OrderTableLayout extends Component {
  static propTypes = {
    order: PropTypes.object.isRequired,
    keepExchangeOrdersFromReturnCancel: PropTypes.bool.isRequired,
    onToggleItemSelect: PropTypes.func.isRequired,
    selectedItems: PropTypes.array.isRequired,
    resetSelectedItems: PropTypes.func.isRequired,
    handleReturnItems: PropTypes.func.isRequired,
    handleExchangeItems: PropTypes.func.isRequired,
    handleCancelItems: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.firstRadioButtonRef = createRef();
    // Create refs here for the return and cancel cases
    const exchangeItemButtonRef = createRef();
    this.buttonsHeaderRefs = {
      exchangeItemButtonRef
    };
  }

  state = { step: ORDER_PAGE_STEPS.DEFAULT_STEP };

  componentDidMount() {
    const { step } = this.state;
    const {
      order,
      resetSelectedItems,
      previousOrderAction,
      selectedItems,
      location: { search } = {},
      keepExchangeOrdersFromReturnCancel
    } = this.props;
    const returnMethod = new URLSearchParams(search).get('returnMethod');
    const isReturnable = isOrderReturnable(order.lineItems, keepExchangeOrdersFromReturnCancel);

    if (selectedItems.length > 0 && returnMethod === 'exchange') {
      this.setState({ ...this.state, step: ORDER_PAGE_STEPS.EXCHANGE_STEP });
    } else if (returnMethod === 'return' && isReturnable) {
      this.setState({ ...this.state, step: ORDER_PAGE_STEPS.RETURN_STEP });
    }

    if (step === ORDER_PAGE_STEPS.EXCHANGE_STEP) {
      track(() => [pvSelectItemsToExchange, { order }]);
    }
    this.toggleInitialUI(previousOrderAction, resetSelectedItems, search);
  }

  componentDidUpdate(prevProps, prevState) {
    const { step } = this.state;
    const { order, selectedItems, location: { search } = {}, keepExchangeOrdersFromReturnCancel } = this.props;
    const { order: prevOrder, selectedItem: prevSelectedItems } = prevProps;
    const { step: prevStep } = prevState;
    const returnMethod = new URLSearchParams(search).get('returnMethod');
    const isReturnable = isOrderReturnable(order.lineItems, keepExchangeOrdersFromReturnCancel);

    if (step === ORDER_PAGE_STEPS.EXCHANGE_STEP) {
      if (order !== prevOrder || step !== prevStep) {
        track(() => [pvSelectItemsToExchange, { order }]);
      }
    }

    if (selectedItems.length > 0 && !deepEqual(selectedItems, prevSelectedItems) && prevStep === ORDER_PAGE_STEPS.DEFAULT_STEP) {
      if (returnMethod === 'exchange') {
        this.setState({ ...this.state, step: ORDER_PAGE_STEPS.EXCHANGE_STEP });
      } else if (returnMethod === 'return' && isReturnable) {
        this.setState({ ...this.state, step: ORDER_PAGE_STEPS.RETURN_STEP });
      }
    }

    // only fire this tracking for mobile views
    if (!isDesktop()) {
      const {
        order,
        shipmentTracking: { trackingInfo },
        updateOrderWithTrackingInfo
      } = this.props;

      const {
        shipmentTracking: { trackingInfo: prevTrackingInfo }
      } = prevProps;

      if (trackingInfo.length !== prevTrackingInfo.length) {
        updateOrderWithTrackingInfo(order, trackingInfo);
      }
    }

    // handle the focus for return and cancel here
    if (!this.props.selectedItems.length) {
      if (step === ORDER_PAGE_STEPS.EXCHANGE_STEP) {
        this.firstRadioButtonRef.current?.focus();
      }
    } else {
      this.resetSelectedItemsIfMixedReturn();
      if (step === ORDER_PAGE_STEPS.EXCHANGE_STEP) {
        this.buttonsHeaderRefs.exchangeItemButtonRef.current?.focus();
      }
    }
  }

  toggleInitialUI = (previousOrderAction, resetSelectedItems, search) => {
    switch (previousOrderAction) {
      case ORDER_ACTION_NONE:
        const preselectedItems = new URLSearchParams(search).get('preselectedItems');
        !preselectedItems && resetSelectedItems();
        break;
      case ORDER_ACTION_RETURN:
        this.tableHeaderReturnAction();
        break;

      case ORDER_ACTION_EXCHANGE:
        this.tableHeaderExchangeAction();
        break;

      default:
        break;
    }
  };

  tableHeaderCancelAction = () => {
    const { search, pathname } = this.props.location;
    const query = new URLSearchParams(search);
    const preselectedItems = query.get('preselectedItems');
    const returnMethod = query.get('returnMethod');
    if (preselectedItems || returnMethod) {
      this.props.resetSelectedItems();
      this.setState({ ...this.state, step: ORDER_PAGE_STEPS.DEFAULT_STEP });
    } else {
      window.location.href = `${pathname}${search}`;
    }
  };

  tableHeaderExchangeAction = () => {
    const { updateOrderItems, resetSelectedItems, previousOrderAction } = this.props;

    // this is to decide wether we need to keep the previous selections
    previousOrderAction !== ORDER_ACTION_EXCHANGE && resetSelectedItems();

    this.setState({ step: ORDER_PAGE_STEPS.EXCHANGE_STEP });
    updateOrderItems();
  };

  tableHeaderExchangeSubmitAction = () => {
    this.props.handleExchangeItems();
  };

  tableHeaderReturnAction = () => {
    const { updateOrderItems, resetSelectedItems } = this.props;

    resetSelectedItems();
    updateOrderItems();
    this.setState({ ...this.state, step: ORDER_PAGE_STEPS.RETURN_STEP });
  };

  tableHeaderReturnSubmitAction = () => {
    this.props.handleReturnItems();
  };

  tableHeaderCancelItemsAction = () => {
    this.props.handleCancelItems();
  };
  resetSelectedItemsIfMixedReturn = () => {
    const { selectedItems } = this.props;

    if (selectedItems?.length > 1) {
      const isAnyItemVersional = selectedItems.some(item => getMarketNameViaMerchantId(item.merchantId) === 'versional');
      const isAnyItemZappos = selectedItems.some(item => getMarketNameViaMerchantId(item.merchantId) === 'zappos');

      //preventing preselection of items when they are mixed
      const isMixedSelectedItems = isAnyItemZappos && isAnyItemVersional;
      if (isMixedSelectedItems) {
        this.props.resetSelectedItems();
      }
    }
  };

  // makes a table row for an item, split it out this way so that its easier
  // to swap between non shipping dates rows vs items grouped by shipment
  makeItemRow = ({ lineItem, index }) => {
    const {
      order,
      onToggleItemSelect,
      onSelectFitQuestion,
      fitItems,
      isFitSurveyLoading,
      isOrderItemsUpdating,
      keepExchangeOrdersFromReturnCancel,
      isVip,
      pageType,
      isGuest,
      isStoreCreditEnabled,
      isWithinReturnWindow,
      isGrandFatheredOutOfStoreCreditReturnWindow
    } = this.props;
    const { lineItems, orderId } = order;
    const {
      product,
      amount,
      finalSale,
      merchantId,
      giftCard,
      giftWrapped,
      giftMessage,
      legacy,
      returnLabelUrl,
      shipment,
      returnDetails: { planName = '' } = {},
      exchangeable,
      itemUniqueId,
      returnDetails,
      egcInfo: { emailAddress, deliveryDisplayDate, message, from } = {}
    } = lineItem;

    const {
      marketplace: {
        returns: {
          showImprovedReturnTrackingStatus,
          returnWindowLength: marketplaceReturnWindowLength,
          enableCancelReturn,
          storeCreditEnabledReturnWindowLength
        }
      }
    } = this.context;

    // shipment comes back as null sometimes so this covers that case
    const { id: shipmentId, trackingNumber, compositeStatus: shipmentCompositeStatus } = shipment || {};

    const { stockId, asin, name } = product;
    const { indicator, id } = fitItems.find(item => item.asin === asin) || {};
    const isReturnWithQRCode = ReturnsUtils.isReturnWithQRCode(planName);
    const { cancellable: isReturnCancellable, contractUnitId, returnTrackingStatus } = returnDetails || {};
    const state = showImprovedReturnTrackingStatus ? applyReturnTrackingStatusToLegacyStatus(lineItem.state, returnTrackingStatus) : lineItem.state;
    const isFitSurveyExempt = giftCard || isFitExempt(state) || merchantId !== ZAPPOS_MERCHANTID;
    const firstSelectableIndex = lineItems.findIndex(lineItem => lineItem.exchangeable);
    const compositeStatus = showImprovedReturnTrackingStatus
      ? applyReturnTrackingStatusToCompositeStatus(lineItem.compositeStatus, returnTrackingStatus)
      : lineItem.compositeStatus;
    const convertedCompositeStatus = getLegacyStatusFromCompositeStatus(compositeStatus);
    const hideReview = merchantId !== ZAPPOS_MERCHANTID || !hasShipmentBeenDelivered(getLegacyStatusFromCompositeStatus(shipmentCompositeStatus));

    // TODO remove once MyAccount can handle the pickup status.
    const isUpsPickupReturn = planName?.includes('Pickup');

    // Prepend link with `/` to prevent URL becoming `/orders/returns` and 404ing
    const cancelReturnUrl = ReturnsUtils.getCancelReturnUrl(orderId, contractUnitId && [contractUnitId], isUpsPickupReturn, true);
    const showCancelReturnLink = isReturnCancellable && enableCancelReturn && cancelReturnUrl;
    const showGiftWrapIndicator = giftWrapped || giftMessage;

    const onCancelReturnClick = e => {
      const { orderHistoryActionType } = e.currentTarget.dataset;
      track(() => [
        evOrderHistoryCancelReturnClick,
        {
          asin,
          orderHistoryActionType,
          planName,
          returnTrackingStatus,
          sourcePage: getAmethystPageType(pageType)
        }
      ]);
    };

    const returnByText = (() => {
      if (finalSale) {
        return (
          <>
            <InfoOutlineSmallIcon size={16} /> Final sale,
            <br /> no returns
          </>
        );
      }

      if (
        lineItem.compositeStatus === CS_CANCELLED ||
        lineItem.compositeStatus === CS_DECLINED_CANCELLED ||
        lineItem.compositeStatus === CS_RETURNED ||
        lineItem.compositeStatus === CS_COMPLETED
      ) {
        return null;
      }

      //until this is enforced on the backend must manually check for guest orders
      if ((isGuest || isStoreCreditEnabled) && !isGrandFatheredOutOfStoreCreditReturnWindow) {
        const orderDate = safeParseOrderDate(order.orderDate);
        const returnByDate = addDaysToDateAndFormat(orderDate, storeCreditEnabledReturnWindowLength);
        return isWithinReturnWindow ? (
          returnByDate
        ) : (
          <>
            Return window ended on <br /> {returnByDate}
          </>
        );
      }

      const orderDate = safeParseOrderDate(order.orderDate);
      const returnByDate = addDaysToDateAndFormat(orderDate, marketplaceReturnWindowLength);
      return isTodayWithinNDaysAfterDate(orderDate, marketplaceReturnWindowLength) ? (
        returnByDate
      ) : (
        <>
          Return window ended on <br /> {returnByDate}
        </>
      );
    })();

    return (
      <MartyContext.Consumer key={itemUniqueId}>
        {context => {
          const {
            testId,
            marketplace: {
              phoneNumber,
              phoneNumberVip,
              desktopBaseUrl,
              dataMaskPiiElements,
              account: { showReturnLabelLink },
              pdp: { showFitSurvey }
            }
          } = context;

          const { selectedItems } = this.props;

          const selectionColumnProps = {
            isOrderItemsUpdating,
            lineItem,
            onToggleItemSelect,
            orderId,
            selectedItems,
            step: this.state.step,
            testId
          };

          const cltNumber = isVip ? phoneNumberVip : phoneNumber;

          const orderTableRowClassName = cn(css.item, {
            [css.withCheckboxes]: shouldDisplayCheckboxesColumn(this.state.step, lineItems, keepExchangeOrdersFromReturnCancel)
          });

          return (
            <tr className={orderTableRowClassName} data-test-id={testId('orderTableRow')}>
              {shouldDisplayCheckboxesColumn(this.state.step, lineItems, keepExchangeOrdersFromReturnCancel) &&
                (index === firstSelectableIndex ? (
                  <OrderTableSelectionColumn {...selectionColumnProps} ref={this.firstRadioButtonRef} />
                ) : (
                  <OrderTableSelectionColumn {...selectionColumnProps} />
                ))}

              <td className={css.items}>
                <OrderProductForExchanges
                  merchantId={merchantId}
                  finalSale={finalSale}
                  price={amount}
                  product={product}
                  isLegacy={legacy}
                  lineItemState={convertedCompositeStatus}
                  hideWriteReview={hideReview}
                  itemUniqueId={itemUniqueId}
                  planName={planName}
                  email={emailAddress}
                  deliveryDisplayDate={deliveryDisplayDate}
                  message={message}
                  from={from}
                />
                {showGiftWrapIndicator && <GiftWrapIndicator giftWrapped={giftWrapped} giftMessage={giftMessage} className={css.giftWrapRow} />}
              </td>

              <td className={cn(css.returnBy, 'zappos:body-m')}>{returnByText}</td>

              {/* Since we are already showing the status above the orders no need to display per item */}
              <td className={css.status}>
                <div className={css.mobileHidden}>
                  {renderItemStatus(
                    trackingNumber,
                    legacy,
                    convertedCompositeStatus,
                    orderId,
                    name,
                    shipmentId,
                    this.state.step,
                    exchangeable,
                    cltNumber,
                    compositeStatus,
                    testId,
                    order,
                    planName
                  )}
                </div>
                {/* Needed a way to render the status parts separate from return button for new experience */}
                {!isUpsPickupReturn && showReturnLabelLink && returnLabelUrl && (
                  <div>
                    <a
                      className={css.printLabelLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-test-id={testId('printLabelLink')}
                      href={`${desktopBaseUrl}${returnLabelUrl}&displayInfo=true`}
                    >{`${isReturnWithQRCode ? 'View Return Code' : 'Print Return Label'}`}</a>
                  </div>
                )}

                {showCancelReturnLink && (
                  <Link
                    to={cancelReturnUrl}
                    className={css.actionLink}
                    data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.ORDER_HISTORY_CANCEL_RETURN}
                    onClick={onCancelReturnClick}
                    data-test-id={testId('cancelReturnLink')}
                  >
                    Cancel Return
                  </Link>
                )}
              </td>

              {showFitSurvey && (
                <td className={css.fit} data-stockid={stockId} data-asin={asin} data-id={id}>
                  {!isFitSurveyExempt && (
                    <>
                      <p className="zappos:body-s">How was the fit?</p>
                      <FitSurveyOptions onSelectFitQuestion={onSelectFitQuestion} indicator={indicator} isFitSurveyLoading={isFitSurveyLoading} />
                    </>
                  )}
                </td>
              )}

              <td className={cn(css.price, 'zappos:body-m-medium')} data-test-id={testId('itemPrice')} data-cs-mask={dataMaskPiiElements}>
                {amount}
              </td>
            </tr>
          );
        }}
      </MartyContext.Consumer>
    );
  };

  render() {
    return (
      <MartyContext.Consumer>
        {context => {
          const {
            marketplace: {
              phoneNumber,
              phoneNumberVip,
              account: { canExchange },
              pdp: { showFitSurvey }
            }
          } = context;

          this.context = context;

          const {
            order,
            keepExchangeOrdersFromReturnCancel,
            selectedItems,
            isOrderItemsUpdating,
            isVip,
            isStoreCreditEnabled,
            isGuest,
            isWithinReturnWindow
          } = this.props;

          const cltNumber = isVip ? phoneNumberVip : phoneNumber;

          const { orderId, lineItems } = order;
          const itemsByShipmentId = toArray(groupBy(lineItems, lineItem => lineItem.shipment?.id || 'other'));
          const isReturnable = isOrderReturnable(order.lineItems, keepExchangeOrdersFromReturnCancel);
          const calculatedIsReturnable = isGuest && isStoreCreditEnabled ? isReturnable && isWithinReturnWindow : isReturnable;

          return (
            <div className={css.container}>
              {isOrderGeneratedByExchange(order) &&
                (isOrderReturnable(order.lineItems, false) || isOrderCancellable(order.lineItems, false)) &&
                keepExchangeOrdersFromReturnCancel && (
                  <div className={css.exchangeMessage}>
                    <p>
                      To cancel or return this exchanged item, please contact our Customer Loyalty Team at <strong>{cltNumber}</strong>.
                    </p>
                  </div>
                )}

              {(isOrderExchangeable(order.lineItems) ||
                isOrderCancellable(order.lineItems, keepExchangeOrdersFromReturnCancel) ||
                calculatedIsReturnable) && (
                <OrderTableActionButtonsHeader
                  cancelAction={this.tableHeaderCancelAction}
                  keepExchangeOrdersFromReturnCancel={keepExchangeOrdersFromReturnCancel}
                  exchangeSelectionAction={this.tableHeaderExchangeAction}
                  exchangeSubmitAction={this.tableHeaderExchangeSubmitAction}
                  returnSelectionAction={this.tableHeaderReturnAction}
                  returnSubmitAction={this.tableHeaderReturnSubmitAction}
                  cancelItemsSubmitAction={this.tableHeaderCancelItemsAction}
                  order={order}
                  updateOrderItems={this.props.updateOrderItems}
                  isOrderItemsUpdating={isOrderItemsUpdating}
                  currentStep={this.state.step}
                  previousAction={this.props.previousOrderAction}
                  selectedItems={selectedItems}
                  ref={this.buttonsHeaderRefs}
                  canExchange={canExchange}
                  isStoreCreditEnabled={isStoreCreditEnabled}
                  isGuest={isGuest}
                  isWithinReturnWindow={isWithinReturnWindow}
                />
              )}

              <h2>Items Summary</h2>
              {/* Desktop */}
              <table className={css.orderItems}>
                <caption>List of items ordered</caption>
                <thead>
                  <tr>
                    {shouldDisplayCheckboxesColumn(this.state.step, lineItems, keepExchangeOrdersFromReturnCancel) && (
                      <th className={css.state}></th>
                    )}
                    <th className={cn(css.items, 'zappos:body-xl-bold')}>Items</th>
                    <th className={cn(css.returnBy, 'zappos:body-xl-bold')}>Return By</th>
                    <th className={cn(css.status, 'zappos:body-xl-bold')}>Status</th>
                    {showFitSurvey && <th className={cn(css.fit, 'zappos:body-xl-bold')} />}
                    <th className={cn(css.price, 'zappos:body-xl-bold')}>Price</th>
                  </tr>
                </thead>

                <tbody>
                  {itemsByShipmentId.map(lineItems => {
                    const { id: shipmentId } = lineItems[0].shipment;
                    const { compositeStatus, trackingAvailable } = getCurrentShipment(shipmentId, order.shipments);
                    const currentShipmentNumber = getCurrentShipmentNumber(shipmentId, order.shipments);
                    const shipmentMessage = getCurrentShipmentMessage(shipmentId, order.shipments);
                    const status = getLegacyStatusFromCompositeStatus(compositeStatus);
                    return (
                      <Fragment key={`${shipmentId}`}>
                        {
                          <tr>
                            <td colSpan="5" className={css.mobileShippingStatus}>
                              <ShippingStatus
                                status={status}
                                orderId={orderId}
                                onShipmentClick={handleShipmentClick.bind(this, order, shipmentId, compositeStatus)}
                                shipmentId={shipmentId}
                                totalShipments={getTotalShipmentsNumber(order.shipments)}
                                currentShipmentNumber={currentShipmentNumber}
                                trackingAvailable={trackingAvailable}
                                shipmentMessage={shipmentMessage}
                              ></ShippingStatus>
                            </td>
                          </tr>
                        }
                        {lineItems.map((lineItem, index) => this.makeItemRow({ lineItem, index }))}
                        <tr className={css.shipmentDivider}>
                          <td colSpan="5"></td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }}
      </MartyContext.Consumer>
    );
  }
}

const mapStateToProps = state => ({
  previousOrderAction: state.account.previousOrderAction,
  order: state.order.order,
  shipmentTracking: state.shipmentTracking,
  location: state.router.location,
  isVip: state.rewards?.rewardsInfo?.isVipOrConsented,
  pageType: state.pageView.pageType
});

export default connect(mapStateToProps, {
  updateOrderWithTrackingInfo,
  getIsExchangeEligible
})(OrderTableLayout);
